import { S } from './surfaces';
import { BLOCKS, TILE_SIZE } from '../config';
import { booleanTree, bufferLines } from '../geom/clip';
import type { Poly } from '../geom/polygon';
import type { GraphData } from '../roads/graph';
import { surfaceHeight } from '../terrain/height';
import { MeshBuilder } from './builder';
import { addDraped } from './drape';

/** Road width used for rendering: twice the block setback to the kerb (SPEC §4.2; standardWidth 1190). */
export const ROAD_WIDTH = BLOCKS.roadOutset * 2;

export const roadZ = (seed: number) => (x: number, y: number) => surfaceHeight(seed, x, y) + BLOCKS.roadLift;

/**
 * One road surface per tile: every centreline of the 3×3 tile window buffered by half the road width,
 * unioned, clipped to this tile's square and draped on the terrain. A single polygon per tile means no
 * overlapping ribbons or junction patches, and neighbours meet exactly at the shared tile edge.
 * (The reference draws roads per block as block minus sidewalk, SPEC §4.2.)
 */
export function buildRoadMesh(seed: number, own: GraphData, window: GraphData[], tx: number, ty: number, cell: number = BLOCKS.drapeCell): MeshBuilder {
  const mb = new MeshBuilder();
  const lines: Poly[] = [];
  for (const g of window) {
    for (let i = 0; i < g.edges.length; i += 2) {
      const a = g.edges[i];
      const b = g.edges[i + 1];
      lines.push([
        { x: g.nodes[a * 2], y: g.nodes[a * 2 + 1] },
        { x: g.nodes[b * 2], y: g.nodes[b * 2 + 1] },
      ]);
    }
  }
  if (lines.length === 0) return mb;
  const h = TILE_SIZE / 2;
  const cx = tx * TILE_SIZE;
  const cy = ty * TILE_SIZE;
  const square: Poly = [
    { x: cx - h, y: cy - h },
    { x: cx + h, y: cy - h },
    { x: cx + h, y: cy + h },
    { x: cx - h, y: cy + h },
  ];
  const buffered = bufferLines(lines, ROAD_WIDTH / 2);
  const z = roadZ(seed);
  mb.surface = S.asphalt;
  for (const node of booleanTree('intersection', buffered, [square])) addDraped(mb, node.outer, node.holes, z, [0.31, 0.31, 0.32], cell);
  mb.surface = S.none;
  void own; // markings: decals.ts

  return mb;
}
